import "@/App.css";
import "@/i18n";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import MarketingApp, { isMarketingHost } from "@/marketing/MarketingApp";
import Login from "@/pages/Login";
import Layout from "@/components/Layout";
import SmartInstallBanner from "@/components/SmartInstallBanner";
import PinPanelDock from "@/components/PinPanelDock";
import { Toaster } from "sonner";

// ── Route-level code splitting ──────────────────────────────────────────────
// Every page below loads on demand. This keeps the initial bundle small so
// the client portal opens fast on mobile — phones no longer download the
// whole admin suite (Form 24G, eTDS, accounting, reports…) just to log in.
const ChangePassword = lazy(() => import("@/pages/ChangePassword"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const FirmDashboard = lazy(() => import("@/pages/FirmDashboard"));
const NoticeBoardAdmin = lazy(() => import("@/pages/NoticeBoardAdmin"));
const Tickets = lazy(() => import("@/pages/Tickets"));
const Clients = lazy(() => import("@/pages/Users").then((m) => ({ default: m.Clients })));
const Staff = lazy(() => import("@/pages/Users").then((m) => ({ default: m.Staff })));
const Vendors = lazy(() => import("@/pages/Users").then((m) => ({ default: m.Vendors })));
const Billing = lazy(() => import("@/pages/Billing"));
const Commissions = lazy(() => import("@/pages/Commissions"));
const MyDocuments = lazy(() => import("@/pages/MyDocuments"));
const Profile = lazy(() => import("@/pages/Profile"));
const ClientFiles = lazy(() => import("@/pages/ClientFiles"));
const MyFiles = lazy(() => import("@/pages/MyFiles"));
const MyInvoices = lazy(() => import("@/pages/MyInvoices"));
const Services = lazy(() => import("@/pages/Services"));
const Documents = lazy(() => import("@/pages/Documents"));
const CompletedTickets = lazy(() => import("@/pages/CompletedTickets"));
const CompanyProfile = lazy(() => import("@/pages/CompanyProfile"));
const Settings = lazy(() => import("@/pages/Settings"));
const Receipts = lazy(() => import("@/pages/Receipts"));
const Transactions = lazy(() => import("@/pages/Transactions"));
const Notifications = lazy(() => import("@/pages/Notifications"));
const Chat = lazy(() => import("@/pages/Chat"));
const ClientChat = lazy(() => import("@/pages/ClientChat"));
const ClientBankStatements = lazy(() => import("@/pages/ClientBankStatements"));
const ChatBot = lazy(() => import("@/pages/ChatBot"));
const ClientsDirectory = lazy(() => import("@/pages/ClientsDirectory"));
const Accounting = lazy(() => import("@/pages/Accounting"));
const VyaparImport = lazy(() => import("@/pages/accounting/VyaparImport"));
const BankTally = lazy(() => import("@/pages/accounting/BankTally"));
const CashTally = lazy(() => import("@/pages/accounting/CashTally"));
const LedgerMixer = lazy(() => import("@/pages/accounting/LedgerMixer"));
const TransactionEdit = lazy(() => import("@/pages/accounting/TransactionEdit"));
const BroadcastCenter = lazy(() => import("@/pages/BroadcastCenter"));
const DayBook = lazy(() => import("@/pages/DayBook"));
const GstReturns = lazy(() => import("@/pages/GstReturns"));
const GstFilings = lazy(() => import("@/pages/gst/GstFilings"));
const GstStatementEditor = lazy(() => import("@/pages/gst/GstStatementEditor"));
const Kalpana = lazy(() => import("@/pages/Kalpana"));
const AiSupervisor = lazy(() => import("@/pages/AiSupervisor"));
const KalpanaSettings = lazy(() => import("@/pages/KalpanaSettings"));
const ClientDuplicates = lazy(() => import("@/pages/ClientDuplicates"));
const Form24GDashboard = lazy(() => import("@/pages/form24g/Form24GDashboard"));
const ETDSReturns = lazy(() => import("@/pages/ETDSReturns"));
const ETDSEditor = lazy(() => import("@/pages/etds/ETDSEditor"));
const PANMaster = lazy(() => import("@/pages/etds/PANMaster"));
const ETDSSummary = lazy(() => import("@/pages/etds/ETDSSummary"));
const Form24GWorkspace = lazy(() => import("@/pages/form24g/Form24GWorkspace"));
const Form24GStatementDetail = lazy(() => import("@/pages/form24g/Form24GStatementDetail"));
const WorkstationHome = lazy(() => import("@/pages/workstation/WorkstationHome"));
const Quests = lazy(() => import("@/pages/Quests"));
const Reports = lazy(() => import("@/pages/Reports"));
const WorkChainReport = lazy(() => import("@/pages/reports/WorkChainReport"));
const LinkBackfill = lazy(() => import("@/pages/admin/LinkBackfill"));
const LeadsInbox = lazy(() => import("@/pages/LeadsInbox"));
const CommunicationHub = lazy(() => import("@/pages/CommunicationHub"));
const PublicInvoice = lazy(() => import("@/pages/PublicInvoice"));
const PublicReceipt = lazy(() => import("@/pages/PublicReceipt"));
const ActivityLog = lazy(() => import("@/pages/ActivityLog"));
const DocumentFeed = lazy(() => import("@/pages/DocumentFeed"));
const Vault = lazy(() => import("@/pages/Vault"));
const Compliance = lazy(() => import("@/pages/Compliance"));
const NsdlReport = lazy(() => import("@/pages/NsdlReport"));
const NsdlPublic = lazy(() => import("@/pages/NsdlPublic"));
const NsdlBackfill = lazy(() => import("@/pages/NsdlBackfill"));
const Folios = lazy(() => import("@/pages/Folios"));
const WorkOrders = lazy(() => import("@/pages/WorkOrders"));
const Expenditures = lazy(() => import("@/pages/Expenditures"));
const MarketingCMS = lazy(() => import("@/pages/MarketingCMS"));
const OtherNeedful = lazy(() => import("@/pages/OtherNeedful"));
const Dropdowns = lazy(() => import("@/pages/Dropdowns"));
const Install = lazy(() => import("@/pages/Install"));
const ShareTarget = lazy(() => import("@/pages/ShareTarget"));
const VendorAnalytics = lazy(() => import("@/pages/VendorAnalytics"));
const VendorReports = lazy(() => import("@/pages/VendorReports"));
const Groups = lazy(() => import("@/pages/Groups"));
const ClientsRecency = lazy(() => import("@/pages/ClientsRecency"));
const WhatsAppSettings = lazy(() => import("@/pages/WhatsAppSettings"));
const VyaparArchive = lazy(() => import("@/pages/VyaparArchive"));
const SalesDocs = lazy(() => import("@/pages/SalesDocs"));
const PriceListDefault = lazy(() => import("@/pages/PriceListDefault"));
const BulkCatalog = lazy(() => import("@/pages/BulkCatalog"));
const BulkRequiredDocs = lazy(() => import("@/pages/BulkRequiredDocs"));
const ClientMyPriceList = lazy(() => import("@/pages/ClientMyPriceList"));
const ClientControlPanel = lazy(() => import("@/pages/ClientControlPanel"));
const ClientPriceListPage = lazy(() => import("@/pages/ClientPriceListPage"));
const ClientInvoiceHub = lazy(() => import("@/pages/ClientInvoiceHub"));
const ClientReceiptHub = lazy(() => import("@/pages/ClientReceiptHub"));
const ClientStatement = lazy(() => import("@/pages/ClientStatement"));
const ClientNewTicket = lazy(() => import("@/pages/ClientNewTicket"));
const MyNewTicket = lazy(() => import("@/pages/MyNewTicket"));
const ClientMyServices = lazy(() => import("@/pages/ClientMyServices"));
const Families = lazy(() => import("@/pages/Families"));
const Rosters = lazy(() => import("@/pages/Rosters"));
const MyFamily = lazy(() => import("@/pages/MyFamily"));
const ClientServiceWorkspace = lazy(() => import("@/pages/ClientServiceWorkspace"));
const ClientPureAgent = lazy(() => import("@/pages/ClientPureAgent"));
const ClientCredentialsVault = lazy(() => import("@/pages/ClientCredentialsVault"));
const ClientServicesAvailed = lazy(() => import("@/pages/ClientServicesAvailed"));
const ClientDocumentFeed = lazy(() => import("@/pages/ClientDocumentFeed"));
const ClientStaffAssigned = lazy(() => import("@/pages/ClientStaffAssigned"));
const ClientProfilePage = lazy(() => import("@/pages/ClientProfilePage"));
const ClientInternalChat = lazy(() => import("@/pages/ClientInternalChat"));
const ClientEmailHistory = lazy(() => import("@/pages/ClientEmailHistory"));
const InvoicePage = lazy(() => import("@/pages/billing/InvoicePage"));
const Terms = lazy(() => import("@/pages/legal/Terms"));
const Privacy = lazy(() => import("@/pages/legal/Privacy"));
const Refunds = lazy(() => import("@/pages/legal/Refunds"));
const Contact = lazy(() => import("@/pages/legal/Contact"));
const KalpanaBubble = lazy(() => import("@/components/Kalpana/KalpanaBubble"));

// Stable role constants — avoid creating a new array on every <Protected> render.
const ROLES_STAFF_ADMIN = ["admin", "staff"];
const ROLES_FIRM_AND_VENDOR = ["admin", "staff", "vendor"];
const ROLES_ALL_LOGGED_IN = ["admin", "staff", "vendor", "client"];
const ROLES_ALL = ["admin", "staff", "vendor", "client"];
const ROLES_ADMIN_ONLY = ["admin"];
const ROLES_CLIENT_ONLY = ["client"];

const PageLoader = () => (
    <div className="min-h-[40vh] flex items-center justify-center text-[#71717A]">
        <div className="flex items-center gap-2 text-sm">
            <span className="inline-block w-4 h-4 border-2 border-slate-300 border-t-slate-700 rounded-full animate-spin" />
            Loading…
        </div>
    </div>
);

function Protected({ children, roles }) {
    const { user, impersonatedBy } = useAuth();
    if (user === null) {
        return (
            <div className="min-h-screen flex items-center justify-center text-[#71717A]">Loading...</div>
        );
    }
    if (user === false) return <Navigate to="/login" replace />;
    // Skip change-password gate during preview-as — admin should see the real UI
    if (!impersonatedBy && user.must_change_password && window.location.pathname !== "/change-password") {
        return <Navigate to="/change-password" replace />;
    }
    if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
    // ?embed=1 = render BARE (no sidebar / no header / no chrome). Used by
    // the floating Passbook popup on the Expenditures page so the iframe
    // shows only the passbook content, not the whole app shell.
    const isEmbed = new URLSearchParams(window.location.search).get("embed") === "1";
    if (isEmbed) return <div className="embed-host p-3">{children}</div>;
    return <Layout>{children}</Layout>;
}

function PublicOnly({ children }) {
    const { user, impersonatedBy } = useAuth();
    if (user === null) return <div className="min-h-screen flex items-center justify-center text-[#71717A]">Loading...</div>;
    if (user && (impersonatedBy || !user.must_change_password)) return <Navigate to="/" replace />;
    return children;
}

function AppRoutes() {
    return (
        <Suspense fallback={<PageLoader />}>
        <Routes>
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/refunds" element={<Refunds />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/install" element={<Install />} />
            {/* Public tokenised invoice — opened when a client clicks the
                WhatsApp / Email invoice link. NO auth wall. Renders the same
                "Template - GST" HTML as admin Preview → Print. */}
            <Route path="/public/invoices/:token" element={<PublicInvoice />} />
            <Route path="/public/receipts/:token" element={<PublicReceipt />} />
            <Route path="/change-password" element={
                <ProtectedNoLayout><ChangePassword /></ProtectedNoLayout>
            } />
            <Route path="/" element={<Protected><FirmDashboard /></Protected>} />
            <Route path="/legacy-dashboard" element={<Protected><Dashboard /></Protected>} />
            <Route path="/notices" element={<Protected roles={["admin"]}><NoticeBoardAdmin /></Protected>} />
            <Route path="/tickets" element={<Protected><Tickets /></Protected>} />
            <Route path="/tickets/:ticketId" element={<Protected><Tickets /></Protected>} />
            <Route path="/quests" element={<Protected roles={ROLES_STAFF_ADMIN}><Quests /></Protected>} />
            <Route path="/quests/:questId" element={<Protected roles={ROLES_STAFF_ADMIN}><Quests /></Protected>} />
            <Route path="/reports" element={<Protected roles={ROLES_STAFF_ADMIN}><Reports /></Protected>} />
            <Route path="/reports/work-chain" element={<Protected roles={ROLES_STAFF_ADMIN}><WorkChainReport /></Protected>} />
            <Route path="/admin/link-backfill" element={<Protected roles={["admin"]}><LinkBackfill /></Protected>} />
            <Route path="/leads" element={<Protected roles={ROLES_ALL_LOGGED_IN}><LeadsInbox /></Protected>} />
            <Route path="/communication-hub" element={<Protected roles={ROLES_STAFF_ADMIN}><CommunicationHub /></Protected>} />
            <Route path="/marketing-cms" element={<Protected roles={ROLES_STAFF_ADMIN}><MarketingCMS /></Protected>} />
            <Route path="/needful" element={<Protected roles={["client", "vendor", "staff", "admin"]}><OtherNeedful /></Protected>} />
            <Route path="/clients" element={<Protected roles={ROLES_FIRM_AND_VENDOR}><Clients /></Protected>} />
            <Route path="/clients/:id/files" element={<Protected roles={ROLES_ALL}><ClientFiles /></Protected>} />
            <Route path="/clients/:id/panel" element={<Protected roles={ROLES_STAFF_ADMIN}><ClientControlPanel /></Protected>} />
            <Route path="/clients/:id/price-list" element={<Protected roles={ROLES_STAFF_ADMIN}><ClientPriceListPage /></Protected>} />
            <Route path="/clients/:id/invoice-hub" element={<Protected roles={ROLES_STAFF_ADMIN}><ClientInvoiceHub /></Protected>} />
            <Route path="/clients/:id/receipt-hub" element={<Protected roles={ROLES_STAFF_ADMIN}><ClientReceiptHub /></Protected>} />
            <Route path="/clients/:id/statement"   element={<Protected roles={ROLES_STAFF_ADMIN}><ClientStatement /></Protected>} />
            <Route path="/clients/:id/new-ticket" element={<Protected><ClientNewTicket /></Protected>} />
            {/* Client-side alias — opens the same wizard for the current client, no id in URL */}
            <Route path="/my-new-ticket" element={<Protected roles={ROLES_CLIENT_ONLY}><MyNewTicket /></Protected>} />
            <Route path="/clients/:id/credentials" element={<Protected roles={ROLES_STAFF_ADMIN}><ClientCredentialsVault /></Protected>} />
            <Route path="/clients/:id/services-availed" element={<Protected roles={ROLES_STAFF_ADMIN}><ClientServicesAvailed /></Protected>} />
            <Route path="/clients/:id/document-feed" element={<Protected roles={ROLES_STAFF_ADMIN}><ClientDocumentFeed /></Protected>} />
            <Route path="/clients/:id/staff-assigned" element={<Protected roles={ROLES_STAFF_ADMIN}><ClientStaffAssigned /></Protected>} />
            <Route path="/clients/:id/profile" element={<Protected roles={ROLES_STAFF_ADMIN}><ClientProfilePage /></Protected>} />
            <Route path="/clients/:id/internal-chat" element={<Protected roles={ROLES_STAFF_ADMIN}><ClientInternalChat /></Protected>} />
            <Route path="/clients/:id/email-history" element={<Protected roles={ROLES_STAFF_ADMIN}><ClientEmailHistory /></Protected>} />
            <Route path="/clients-recency" element={<Protected roles={ROLES_STAFF_ADMIN}><ClientsRecency /></Protected>} />
            <Route path="/admin/whatsapp" element={<Protected roles={["admin"]}><WhatsAppSettings /></Protected>} />
            <Route path="/admin/vyapar-archive" element={<Protected roles={ROLES_STAFF_ADMIN}><VyaparArchive /></Protected>} />
            <Route path="/sales-docs" element={<Protected roles={ROLES_STAFF_ADMIN}><SalesDocs /></Protected>} />
            <Route path="/my-files" element={<Protected roles={ROLES_CLIENT_ONLY}><MyFiles /></Protected>} />
            <Route path="/my-invoices" element={<Protected roles={ROLES_CLIENT_ONLY}><MyInvoices /></Protected>} />
            <Route path="/staff" element={<Protected roles={ROLES_STAFF_ADMIN}><Staff /></Protected>} />
            <Route path="/vendors" element={<Protected roles={ROLES_STAFF_ADMIN}><Vendors /></Protected>} />
            <Route path="/vendors/:vendorId/analytics" element={<Protected roles={ROLES_STAFF_ADMIN}><VendorAnalytics /></Protected>} />
            <Route path="/vendor-reports" element={<Protected roles={ROLES_FIRM_AND_VENDOR}><VendorReports /></Protected>} />
            <Route path="/groups" element={<Protected roles={ROLES_STAFF_ADMIN}><Groups /></Protected>} />
            <Route path="/share-target" element={<Protected><ShareTarget /></Protected>} />
            <Route path="/billing" element={<Protected><Billing /></Protected>} />
            <Route path="/invoices/:id" element={<Protected><InvoicePage /></Protected>} />
            <Route path="/services" element={<Protected><Services /></Protected>} />
            <Route path="/commissions" element={<Protected roles={ROLES_FIRM_AND_VENDOR}><Commissions /></Protected>} />
            <Route path="/my-documents" element={<Protected roles={ROLES_STAFF_ADMIN}><MyDocuments /></Protected>} />
            <Route path="/documents" element={<Protected roles={ROLES_STAFF_ADMIN}><Documents /></Protected>} />
            <Route path="/completed-tickets" element={<Protected roles={ROLES_STAFF_ADMIN}><CompletedTickets /></Protected>} />
            <Route path="/receipts" element={<Protected roles={ROLES_STAFF_ADMIN}><Receipts /></Protected>} />
            <Route path="/transactions" element={<Protected roles={ROLES_STAFF_ADMIN}><Transactions /></Protected>} />
            <Route path="/notifications" element={<Protected roles={ROLES_STAFF_ADMIN}><Notifications /></Protected>} />
            <Route path="/chat" element={<Protected roles={ROLES_ALL}><Chat /></Protected>} />
            <Route path="/client-chat" element={<Protected roles={ROLES_ALL}><ClientChat /></Protected>} />
            <Route path="/clients/:id/bank-statements" element={<Protected roles={["admin", "staff"]}><ClientBankStatements /></Protected>} />
            <Route path="/my-bank-statements" element={<Protected roles={["client"]}><ClientBankStatements /></Protected>} />
            <Route path="/document-feed" element={<Protected roles={ROLES_STAFF_ADMIN}><DocumentFeed /></Protected>} />
            <Route path="/vault" element={<Protected roles={ROLES_STAFF_ADMIN}><Vault /></Protected>} />
            <Route path="/compliance" element={<Protected roles={ROLES_STAFF_ADMIN}><Compliance /></Protected>} />
            <Route path="/nsdl-report" element={<Protected roles={ROLES_STAFF_ADMIN}><NsdlReport /></Protected>} />
            <Route path="/nsdl-backfill" element={<Protected roles={ROLES_STAFF_ADMIN}><NsdlBackfill /></Protected>} />
            <Route path="/folios" element={<Protected roles={ROLES_STAFF_ADMIN}><Folios /></Protected>} />
            <Route path="/work-orders" element={<Protected roles={ROLES_STAFF_ADMIN}><WorkOrders /></Protected>} />
            <Route path="/expenditures" element={<Protected roles={ROLES_STAFF_ADMIN}><Expenditures /></Protected>} />
            <Route path="/nsdl-public/:token" element={<NsdlPublic />} />
            <Route path="/chat-bot" element={<Protected roles={ROLES_STAFF_ADMIN}><ChatBot /></Protected>} />
            <Route path="/clients-directory" element={<Protected roles={ROLES_STAFF_ADMIN}><ClientsDirectory /></Protected>} />
            <Route path="/accounting" element={<Protected roles={["admin", "staff"]}><Accounting /></Protected>} />
            <Route path="/accounting/vyapar-import" element={<Protected roles={["admin", "director"]}><VyaparImport /></Protected>} />
            <Route path="/accounting/bank-tally" element={<Protected roles={["admin", "director"]}><BankTally /></Protected>} />
            <Route path="/accounting/cash-tally" element={<Protected roles={["admin", "director"]}><CashTally /></Protected>} />
            <Route path="/accounting/ledger-mixer" element={<Protected roles={["admin", "director", "staff"]}><LedgerMixer /></Protected>} />
            <Route path="/accounting/edit/:kind/:id" element={<Protected roles={["admin", "director"]}><TransactionEdit /></Protected>} />
            <Route path="/broadcast-center" element={<Protected roles={["admin", "director", "staff"]}><BroadcastCenter /></Protected>} />
            <Route path="/price-lists/default" element={<Protected roles={["admin"]}><PriceListDefault /></Protected>} />
            <Route path="/services-catalog/bulk" element={<Protected roles={["admin", "staff"]}><BulkCatalog /></Protected>} />
            <Route path="/required-documents/bulk" element={<Protected roles={["admin", "staff"]}><BulkRequiredDocs /></Protected>} />
            <Route path="/portal/my-price-list" element={<Protected roles={["client"]}><ClientMyPriceList /></Protected>} />
            <Route path="/portal/my-services" element={<Protected roles={["client"]}><ClientMyServices /></Protected>} />
            <Route path="/portal/my-family" element={<Protected roles={["client"]}><MyFamily /></Protected>} />
            <Route path="/families" element={<Protected roles={ROLES_STAFF_ADMIN}><Families /></Protected>} />
            <Route path="/rosters" element={<Protected roles={ROLES_STAFF_ADMIN}><Rosters /></Protected>} />
            <Route path="/clients/:id/service/:slug" element={<Protected roles={ROLES_STAFF_ADMIN}><ClientServiceWorkspace /></Protected>} />
            <Route path="/clients/:id/pure-agent" element={<Protected roles={ROLES_STAFF_ADMIN}><ClientPureAgent /></Protected>} />
            <Route path="/accounting/daybook" element={<Protected roles={["admin", "staff"]}><DayBook /></Protected>} />
            <Route path="/gst-returns" element={<Protected roles={["admin", "staff"]}><GstReturns /></Protected>} />
            <Route path="/gst-filings" element={<Protected roles={["admin", "staff"]}><GstFilings /></Protected>} />
            <Route path="/gst-filings/:id" element={<Protected roles={["admin", "staff"]}><GstStatementEditor /></Protected>} />
            <Route path="/admin/client-duplicates" element={<Protected roles={["admin", "staff"]}><ClientDuplicates /></Protected>} />
            <Route path="/form24g" element={<Protected roles={["admin", "staff"]}><Form24GDashboard /></Protected>} />
            <Route path="/etds" element={<Protected roles={["admin", "staff"]}><ETDSReturns /></Protected>} />
            <Route path="/etds/pan-master" element={<Protected roles={["admin", "staff"]}><PANMaster /></Protected>} />
            <Route path="/etds/summary" element={<Protected roles={["admin", "staff"]}><ETDSSummary /></Protected>} />
            <Route path="/etds/:id" element={<Protected roles={["admin", "staff"]}><ETDSEditor /></Protected>} />
            <Route path="/form24g/:clientId" element={<Protected roles={["admin", "staff"]}><Form24GWorkspace /></Protected>} />
            <Route path="/form24g/:clientId/statements/:statementId" element={<Protected roles={["admin", "staff"]}><Form24GStatementDetail /></Protected>} />
            <Route path="/workstation" element={<Protected roles={["admin", "staff"]}><WorkstationHome /></Protected>} />
            <Route path="/workstation/tds" element={<Navigate to="/etds" replace />} />
            <Route path="/dropdowns" element={<Protected roles={["admin"]}><Dropdowns /></Protected>} />
            <Route path="/company-profile" element={<Protected roles={ROLES_STAFF_ADMIN}><CompanyProfile /></Protected>} />
            <Route path="/settings" element={<Protected roles={ROLES_STAFF_ADMIN}><Settings /></Protected>} />
            <Route path="/profile" element={<Protected><Profile /></Protected>} />
            <Route path="/kalpana" element={<Protected roles={ROLES_ADMIN_ONLY}><Kalpana /></Protected>} />
            <Route path="/activity-log" element={<Protected roles={ROLES_ADMIN_ONLY}><ActivityLog /></Protected>} />
            <Route path="/ai-supervisor" element={<Protected roles={ROLES_STAFF_ADMIN}><AiSupervisor /></Protected>} />
            <Route path="/kalpana/settings" element={<Protected roles={ROLES_ADMIN_ONLY}><KalpanaSettings /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
    );
}

function ProtectedNoLayout({ children }) {
    const { user } = useAuth();
    if (user === null) return <div className="min-h-screen flex items-center justify-center text-[#71717A]">Loading...</div>;
    if (user === false) return <Navigate to="/login" replace />;
    return children;
}

/**
 * RoleBodyTag — mirrors the current user's role onto <body data-role="…">
 * so global CSS can selectively hide the platform "Made with Emergent"
 * badge for staff/vendor/client portals while keeping it visible for admin.
 */
function RoleBodyTag() {
    const { user } = useAuth();
    useEffect(() => {
        const role = user && user.role ? user.role : "anon";
        document.body.dataset.role = role;
        return () => { /* keep last role on unmount */ };
    }, [user]);
    return null;
}

function App() {
    // Marketing site (taxsolutionindia.in) — completely isolated app shell,
    // no AuthProvider, no CRM routes, no Layout chrome.
    if (isMarketingHost()) {
        return <MarketingApp />;
    }
    return (
        <BrowserRouter>
            <AuthProvider>
                <RoleBodyTag />
                <AppRoutes />
                <SmartInstallBanner />
                <Suspense fallback={null}>
                    <KalpanaBubble />
                </Suspense>
                <PinPanelDock />
                <Toaster
                    position="top-right"
                    richColors
                    closeButton
                    toastOptions={{ duration: 3000 }}
                />
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
